import { useEffect, useState, useTransition } from 'react'

import { IndicatorBadgeProps } from './IndicatorBadge.js'
import { indicatorBadgeStyles } from './IndicatorBadge.styles.js'
import { formatCount } from './utils.js'

const outTransition = 'transform 0.15s ease-in, opacity 0.15s ease-in'
const inTransition = 'transform 0.15s ease-out, opacity 0.15s ease-out'

export const IndicatorBadgeFlipper = ({
  count,
  max = 99,
  showZero = false,
  variant = 'danger',
  size = 'md',
  title,
  className,
  ...rest
}: IndicatorBadgeProps) => {
  const [deferredCount, setDeferredCount] = useState(count)
  const [_isPending, startTransition] = useTransition()
  const [hasEnteredAnimation, enterAnimation] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (count === deferredCount || (count > max && deferredCount > max)) {
      return () => {}
    }

    enterAnimation(true)

    const timeout1 = setTimeout(() => {
      startTransition(() => {
        setIsAnimating(true)
      })
    }, 10)

    const timeout2 = setTimeout(() => {
      startTransition(() => {
        setDeferredCount(count)
        setIsAnimating(false)
        enterAnimation(false)
      })
    }, 200)

    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [count])

  if (count === undefined || count === null) {
    return null
  }

  const shouldHide = !showZero && count === 0
  return (
    <div
      className={indicatorBadgeStyles({ variant, size, className })}
      style={{
        position: 'relative',
        overflow: 'hidden',
        transition: outTransition,
        ...(shouldHide
          ? {
              opacity: 0,
            }
          : {}),
      }}
      title={title}
      {...rest}
    >
      {hasEnteredAnimation && !shouldHide && (
        <>
          <Counter
            count={deferredCount}
            max={max}
            style={{
              transition: outTransition,
              ...(isAnimating
                ? {
                    transform: 'translateY(-200%) scale(0.7)',
                    opacity: '0',
                  }
                : {}),
            }}
          />
          <Counter
            count={count}
            max={max}
            style={{
              transition: inTransition,
              transform: 'translateY(100%) scale(0.7)',
              opacity: '0',
              ...(isAnimating
                ? {
                    transform: 'translateY(0) scale(1)',
                    opacity: '1',
                  }
                : {}),
            }}
          />
        </>
      )}
      {!hasEnteredAnimation && <Counter count={deferredCount} max={max} />}
    </div>
  )
}

const Counter = (props: {
  count: number
  max: number
  style?: Record<string, unknown>
}) => {
  const { count, max, style = {} } = props
  return (
    <span
      className="whitespace-nowrap"
      style={{
        display: 'block',
        position: 'absolute',
        textAlign: 'center',
        width: '100%',
        ...style,
      }}
    >
      {formatCount(count, max)}
    </span>
  )
}
